/* Keyboard access for the informational shell. Keep this independent of
   film playback and purchase behavior. The existing gate/menu handlers own
   opening and closing; this module observes their public hidden state. */
(() => {
  'use strict';

  function init() {
    const body = document.body;
    if (!body || body.dataset.ccAccessibility) return;
    body.dataset.ccAccessibility = '1';

    const focusSelector = 'a[href], area[href], button, input, select, textarea, iframe, [contenteditable="true"], [tabindex]';
    const isolated = new Map();
    let active = null;
    let previousFocus = null;
    let recentFocus = null;
    let syncing = false;

    const visible = element => !!(element && element.isConnected &&
      !element.closest('[hidden], [inert]') && element.getClientRects().length);
    const usable = element => visible(element) && !element.disabled;
    const focusables = root => Array.from(root.querySelectorAll(focusSelector))
      .filter(element => usable(element) && element.tabIndex >= 0);

    function focus(element, preventScroll = true) {
      if (!usable(element)) return false;
      if (!element.matches(focusSelector)) element.setAttribute('tabindex', '-1');
      try { element.focus({ preventScroll }); }
      catch (error) { element.focus(); }
      return document.activeElement === element;
    }

    function restoreBackground() {
      isolated.forEach((saved, element) => {
        for (const name of ['inert', 'aria-hidden']) {
          if (saved[name] === null) element.removeAttribute(name);
          else element.setAttribute(name, saved[name]);
        }
      });
      isolated.clear();
    }

    function isolateBackground(root) {
      /* The menu lives inside a Shopify section inside #shell. Isolating
         only body children would also hide the menu from assistive tech. */
      for (let branch = root; branch && branch !== body; branch = branch.parentElement) {
        const parent = branch.parentElement;
        if (!parent) break;
        Array.from(parent.children).forEach(element => {
          if (element === branch || /^(SCRIPT|STYLE|LINK|META)$/.test(element.tagName)) return;
          /* Live regions stay audible: theme.js announces "Added to cart."
             moments before the drawer opens, and inert/aria-hidden on the
             status node would swallow it (Pass 3, 0g). */
          if (element.matches('[aria-live], [role="status"], [role="alert"]')) return;
          if (!isolated.has(element)) {
            isolated.set(element, {
              inert: element.getAttribute('inert'),
              'aria-hidden': element.getAttribute('aria-hidden')
            });
          }
          element.setAttribute('inert', '');
          element.setAttribute('aria-hidden', 'true');
        });
      }
    }

    function currentModal() {
      const gate = document.querySelector('[data-age-gate]');
      if (gate && !gate.hidden && gate.getClientRects().length) return gate;
      const menu = document.querySelector('[data-menu-scrim]');
      if (menu && !menu.hidden && menu.getClientRects().length) return menu;
      /* Pass 3 hotfix 0g: the AJAX cart drawer (snippets/cart-drawer.liquid,
         role="dialog" aria-modal="true" inside [data-cart-scrim]) is a modal
         too. theme.js toggles the same `hidden` hook the observer already
         watches, so the trap needs no new wiring on the cart side. */
      const cart = document.querySelector('[data-cart-scrim]');
      if (cart && !cart.hidden && cart.getClientRects().length) return cart;
      return null;
    }

    function initialFocus(root) {
      const heading = root.querySelector('#gate-state-title:not([hidden])');
      if (usable(heading)) return heading;
      const title = root.querySelector('#gate-title');
      if (usable(title)) return title;
      return root.querySelector('[data-menu-close], [data-cart-close]') || focusables(root)[0] || root;
    }

    function openerFor(modal) {
      if (modal.matches('[data-menu-scrim]')) return document.querySelector('[data-menu-toggle]');
      if (modal.matches('[data-cart-scrim]')) return document.querySelector('[data-cart-open]');
      return document.getElementById('main');
    }

    function containFocus() {
      if (!active) return;
      const target = active.contains(recentFocus) && usable(recentFocus)
        ? recentFocus : initialFocus(active);
      focus(target, false);
    }

    function sync() {
      if (syncing) return;
      syncing = true;
      try {
        const next = currentModal();
        if (next !== active) {
          const closing = active;
          const restoreTo = previousFocus;
          restoreBackground();
          active = next;
          recentFocus = null;
          if (next) {
            previousFocus = openerFor(next);
            /* Move focus before aria-hiding a formerly focused ancestor. */
            if (next.contains(document.activeElement) && usable(document.activeElement)) {
              recentFocus = document.activeElement;
            } else containFocus();
            isolateBackground(next);
          } else if (closing) {
            previousFocus = null;
            /* theme.js's menu and cart closers restore focus to the element
               that opened them before this observer runs; when they already
               have, leave it — refocusing #main here would undo it. */
            const current = document.activeElement;
            const ownerRestored = !!current && current !== body && !closing.contains(current) && usable(current);
            if (!ownerRestored && (!restoreTo || closing.contains(restoreTo) || restoreTo === body || !focus(restoreTo))) {
              focus(document.getElementById('main'));
            }
          }
        }
        if (active) {
          if (!active.contains(document.activeElement) || !usable(document.activeElement)) containFocus();
          isolateBackground(active);
        }
      } finally { syncing = false; }
    }

    document.addEventListener('focusin', event => {
      if (syncing) return;
      sync();
      if (!active) return;
      if (active.contains(event.target) && usable(event.target)) recentFocus = event.target;
      else containFocus();
    }, true);

    document.addEventListener('keydown', event => {
      if (event.key !== 'Tab') return;
      sync();
      if (!active) return;
      const items = focusables(active);
      const index = items.indexOf(document.activeElement);
      if (!items.length) {
        event.preventDefault();
        containFocus();
      } else if (event.shiftKey && index <= 0) {
        event.preventDefault();
        focus(items[items.length - 1], false);
      } else if (!event.shiftKey && (index < 0 || index === items.length - 1)) {
        event.preventDefault();
        focus(items[0], false);
      }
    }, true);

    document.addEventListener('click', event => {
      const link = event.target.closest && event.target.closest('a.skip-link[href^="#"]');
      if (!link || event.defaultPrevented) return;
      const target = document.getElementById(link.hash.slice(1));
      if (target) requestAnimationFrame(() => focus(target));
    });

    const observer = new MutationObserver(sync);
    observer.observe(body, { subtree: true, childList: true, attributes: true, attributeFilter: ['hidden'] });
    document.addEventListener('cc:gate:closed', sync);
    sync();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
